import React, { useEffect, useRef, useState, useCallback } from "react";
import styles from "./index.module.scss";
import { ChevronDown } from "lucide-react";

const MultiSelectDropdown = ({
  options = [],
  selectedValues = {},
  onChange,
  placeholder = "Select options...",
  getOptionLabel = (option) => option,
  getOptionValue = (option) => option,
}) => {
  const wrapperRef = useRef(null);
  const buttonTextRef = useRef(null);
  const countBadgeRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState(
    new Set(Object.keys(selectedValues).filter((key) => selectedValues[key]))
  );
  const [selectedOptionsArray, setSelectedOptionsArray] = useState(
    Object.keys(selectedValues).filter((key) => selectedValues[key])
  );

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const updateButtonText = useCallback(() => {
    if (!buttonTextRef.current) return;

    const count = selectedOptions.size;
    const buttonText = buttonTextRef.current;

    if (count === 0) {
      buttonText.innerHTML = `<span class="placeholder">${placeholder}</span>`;
      if (countBadgeRef.current) {
        countBadgeRef.current.style.display = "none";
      }
    } else {
      const itemsToShow = Math.min(count, 3);
      const displayedItems = selectedOptionsArray.slice(0, itemsToShow);
      const remainingCount = count - itemsToShow;

      let html = '<div class="selected-items">';
      displayedItems.forEach((item) => {
        const option = options.find((opt) => getOptionValue(opt) === item);
        const label = option ? getOptionLabel(option) : item;
        html += `<span class="selected-item">${label}</span>`;
      });
      if (remainingCount > 0) {
        html += `<span class="more-count">+${remainingCount} more</span>`;
      }
      html += "</div>";

      buttonText.innerHTML = html;
      if (countBadgeRef.current) {
        countBadgeRef.current.style.display = "flex";
        countBadgeRef.current.textContent = count;
      }
    }
  }, [selectedOptions, selectedOptionsArray, options, getOptionLabel, getOptionValue, placeholder]);

  useEffect(() => {
    updateButtonText();
  }, [updateButtonText]);

  // Sync selectedOptions with selectedValues prop changes
  useEffect(() => {
    const selected = Object.keys(selectedValues).filter(
      (key) => selectedValues[key]
    );
    setSelectedOptions(new Set(selected));
    setSelectedOptionsArray(selected);
  }, [selectedValues]);

  const handleCheckboxUpdate = (e) => {
    const value = e.target.value;
    const isChecked = e.target.checked;

    setSelectedOptions((prev) => {
      const newSet = new Set(prev);
      if (isChecked) {
        newSet.add(value);
      } else {
        newSet.delete(value);
      }
      return newSet;
    });

    setSelectedOptionsArray((prev) => {
      if (isChecked) {
        return [...prev, value];
      } else {
        return prev.filter((item) => item !== value);
      }
    });

    if (onChange) {
      // Support both function and direct value updates
      if (typeof onChange === 'function') {
        onChange((prev) => ({
          ...prev,
          [value]: isChecked,
        }));
      }
    }
  };

  const handleClearAll = () => {
    setSelectedOptions(new Set());
    setSelectedOptionsArray([]);
    if (onChange) {
      // Support both function and direct value updates
      if (typeof onChange === 'function') {
        onChange((prev) => {
          const clearedValues = { ...prev };
          options.forEach((option) => {
            clearedValues[getOptionValue(option)] = false;
          });
          return clearedValues;
        });
      }
    }
  };

  const handleApply = () => {
    setOpen(false);
  };

  return (
    <div className={styles.multiSelectWrapper} ref={wrapperRef}>
      <div
        className={`${styles.multiSelectButton} ${open ? styles.active : ""}`}
        onClick={() => setOpen((p) => !p)}
      >
        <span ref={buttonTextRef} className={styles.buttonText}>
          <span className={styles.placeholder}>{placeholder}</span>
        </span>
        <div className={styles.buttonRight}>
          <span
            ref={countBadgeRef}
            className={styles.selectedCount}
            style={{ display: "none" }}
          >
            0
          </span>
          <span className={`${styles.arrow} ${open ? styles.rotated : ""}`}>
            <ChevronDown size={18} />
          </span>
        </div>
      </div>

      {open && (
        <div
          className={`${styles.dropdownMenu} ${open ? styles.show : ""}`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={styles.optionsList}>
            {options.map((option) => {
              const value = getOptionValue(option);
              const label = getOptionLabel(option);
              const isSelected = selectedOptions.has(value);
              return (
                <div
                  key={value}
                  className={`${styles.optionItem} ${
                    isSelected ? styles.selected : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    id={`option-${value}`}
                    name={value}
                    value={value}
                    checked={isSelected}
                    onChange={handleCheckboxUpdate}
                  />
                  <label htmlFor={`option-${value}`}>{label}</label>
                </div>
              );
            })}
          </div>
          <div className={styles.footerActions}>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnClear}`}
              onClick={handleClearAll}
            >
              Clear All
            </button>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnApply}`}
              onClick={handleApply}
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;

